/**
 * BumpRadiusControl - Compact brush-radius picker for the bump refinement tool.
 *
 * Renders at the bottom-centre of each editable viewport when bump is active.
 * The annotator can also cycle with [ and ] hotkeys (useViewerHotkeys.ts).
 */

import React from "react";
import { useAtom, useAtomValue } from "jotai";
import { activeMeasurementToolAtom } from "../../atoms/measurementAtoms";
import { bumpRadiusAtom, BUMP_RADII, BumpRadius } from "../../atoms/segmentationAtoms";

export const BumpRadiusControl: React.FC = () => {
  const activeTool = useAtomValue(activeMeasurementToolAtom);
  const [bumpRadius, setBumpRadius] = useAtom(bumpRadiusAtom);

  if (activeTool !== "bump") return null;

  const DOT_SIZES = [3, 5, 8, 11, 14];

  return (
    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-0.5">
      <div className="text-[9px] text-fg-muted bg-black/60 px-2 py-0.5 rounded shadow">
        Press and hold, then drag to push boundary
      </div>
      <div className="flex items-center gap-0">
        {BUMP_RADII.map((r, i) => {
          const isActive = r === bumpRadius;
          const dotSize = DOT_SIZES[i];
          return (
            <button
              key={r}
              onClick={() => setBumpRadius(r as BumpRadius)}
              title={`Bump radius ${r}px`}
              className={`
                flex items-center justify-center
                w-5 h-5 transition-colors
                ${isActive ? "bg-signal" : "bg-surface hover:bg-surface-2"}
                ${i === 0 ? "rounded-l-md" : ""}
                ${i === BUMP_RADII.length - 1 ? "rounded-r-md" : ""}
              `}
            >
              <span
                className={`rounded-full shrink-0 ${isActive ? "bg-white" : "bg-white/60"}`}
                style={{ width: dotSize, height: dotSize }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BumpRadiusControl;
